import React, { useState, useCallback } from "react";
import BillModal from "./BillModalView";
import InvoicePreviewModal from "./InvoicePreviewModal";
import SendInvoiceModal from "./SendInvoiceModel";
import axiosInstance from "./../api/axiosInstance";
import { showGlobalToast } from './../utils/toast';

type BillItem = {
    name: string;
    qty: number;
    price: number;
    discountedPrice: number | null;
};

type BillOrder = {
    orderId: string;
    orderTime: string;
    isShowWarning: boolean;
    subTotal: number;
    grandTotal: number;
    items: BillItem[];
};

type BillSummary = {
    key: string;
    value: string;
    sequenceId: number;
    percentText: string;
    isKeyBold: boolean;
    isValueBold: boolean;
    isCheckBox: boolean;
    isCheckMarked: boolean;
    keyColourCode: string | null;
    valueColourCode: string | null;
};

type BillDetails = {
    apiMessage: string;
    messageNote: string;
    id: string;
    invoiceNo: string;
    customerName: string;
    customerIdentifier: string;
    isMobileCustomer: boolean;
    groupedBy: "table" | "order";
    label: string;
    orders: BillOrder[];
    billSummary: BillSummary[];
    paymentStatus: string;
};

type OrderWithBillGenProps = {
    tableId: string;
    type: 'bill' | 'order'; // 'bill' for bill generation, 'order' for order management
    onBillGenerated?: (bill: BillDetails) => void;
    onBillClosed?: (bill?: BillDetails) => void;
};

const OrderWithBillGen: React.FC<OrderWithBillGenProps> = ({
    tableId,
    type,
    onBillGenerated,
    onBillClosed
}) => {
    // Bill-related state
    const [selectedBill, setSelectedBill] = useState<BillDetails | null>(null);
    const [checkboxState, setCheckboxState] = useState<Record<string, boolean>>({});
    const [changed, setChanged] = useState(false);
    const [invoiceHtml, setInvoiceHtml] = useState<string | null>(null);
    const [showPaymentForm, setShowPaymentForm] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState("");
    const [transactionId, setTransactionId] = useState("");
    const [paymentId, setPaymentId] = useState<number>(0);
    const [isSendInvoiceOpen, setSendInvoiceOpen] = useState(false);

    // Generate bill function
    const generateBill = useCallback(async (billType: "table" | "order", id: string) => {
        try {
            const res = await axiosInstance.get(
                `/Billing/GetBillDetails/${billType}/${id}`
            );
            const bill: BillDetails = res.data;
            setSelectedBill(bill);
            // initialize checkbox state
            const initState: Record<string, boolean> = {};
            bill.billSummary.forEach((s) => {
                if (s.isCheckBox) initState[s.key] = s.isCheckMarked;
            });
            setCheckboxState(initState);
            setChanged(false);

            // Notify parent component
            if (onBillGenerated) {
                onBillGenerated(bill);
            }
        } catch (err: any) {
            console.error("Error fetching bill details:", err);
            showGlobalToast(err.response?.data?.message || 'Error fetching bill details', 'error');
        }
    }, [onBillGenerated]);

    // Bill-related handlers
    const handleCheckboxChange = useCallback((key: string) => {
        setCheckboxState((prev) => {
            const updated = { ...prev, [key]: !prev[key] };
            setChanged(true);
            return updated;
        });
    }, []);

    const handleReGenerate = useCallback(async () => {
        if (!selectedBill) return;
        try {
            const res = await axiosInstance.get(
                `/Billing/GetBillDetails/${selectedBill.groupedBy}/${selectedBill.id}`,
                { params: { includeServiceCharge: checkboxState["Service Charge"] } }
            );
            setSelectedBill(res.data);
            setChanged(false);
            showGlobalToast('Bill regenerated successfully', 'success');
        } catch (err: any) {
            console.error("Error regenerating bill:", err);
            showGlobalToast(err.response?.data?.message ?? 'Error regenerating bill', 'error');
        }
    }, [selectedBill, checkboxState]);

    const handleViewPdf = useCallback(async () => {
        if (!selectedBill) return;
        try {
            const res = await axiosInstance.get(
                `/billing/GetBillHtml/${selectedBill.groupedBy}/${selectedBill.id}/${selectedBill.invoiceNo}`,
                { params: { includeServiceCharge: checkboxState["Service Charge"], paymentId: paymentId ?? 0 } }
            );
            setInvoiceHtml(res.data.htmlContent);
        } catch (err) {
            console.error("Error fetching invoice html:", err);
            showGlobalToast('Error fetching invoice HTML', 'error');
        }
    }, [selectedBill, checkboxState, paymentId]);

    const handleMarkAsPaid = useCallback(async () => {
        if (!selectedBill) return;
        if (!paymentMethod) {
            showGlobalToast('Please select a payment method', 'error');
            return;
        }

        try {
            const response = await axiosInstance.post(`/Payment/MarkAsPaid`, {
                ...selectedBill,
                paymentMethod,
                transactionId,
            });

            // Update UI
            const newPaymentId = response?.data?.paymentId;
            if (newPaymentId && newPaymentId > 0) {
                setPaymentId(newPaymentId);
            }
            setSelectedBill({ ...selectedBill, paymentStatus: "PAID" });
            setShowPaymentForm(false);
            setPaymentMethod("");
            setTransactionId("");
            showGlobalToast('Bill paid successfully', 'success');
        } catch (err: any) {
            console.error("Error marking as paid:", err);
            showGlobalToast(err.response?.data?.message ?? 'Error marking as paid', 'error');
        }
    }, [selectedBill, paymentMethod, transactionId]);

    const handleClosePaymentForm = useCallback(() => {
        if (!selectedBill) return;

        if (selectedBill.paymentStatus === "PAID") {
            // Remove from UI if paid
            setSelectedBill(null);
            if (onBillClosed) {
                onBillClosed(selectedBill);
            }
        } else {
            setSelectedBill(null);
            if (onBillClosed) {
                onBillClosed();
            }
        }
        setShowPaymentForm(false);
    }, [selectedBill, onBillClosed]);

    // Auto-generate bill when component mounts
    React.useEffect(() => {
        if (tableId) {
            const billType = type === 'bill' ? 'table' : 'order';
            generateBill(billType, tableId);
        }
    }, [tableId, type, generateBill]);

    // Expose generateBill function for external use
    React.useImperativeHandle(null, () => ({
        generateBill
    }));

    return (
        <>
            {/* Bill Modal */}
            <BillModal
                selectedBill={selectedBill}
                changed={changed}
                showPaymentForm={showPaymentForm}
                paymentMethod={paymentMethod}
                transactionId={transactionId}
                checkboxState={checkboxState}
                handleClosePaymentForm={handleClosePaymentForm}
                handleCheckboxChange={handleCheckboxChange}
                handleReGenerate={handleReGenerate}
                handleViewPdf={handleViewPdf}
                setSendInvoiceOpen={setSendInvoiceOpen}
                handleMarkAsPaid={handleMarkAsPaid}
                setShowPaymentForm={setShowPaymentForm}
                setPaymentMethod={setPaymentMethod}
                setTransactionId={setTransactionId}
            />

            {/* Invoice Preview Modal */}
            <InvoicePreviewModal
                invoiceHtml={invoiceHtml}
                selectedBill={selectedBill}
                onClose={() => setInvoiceHtml(null)}
            />

            {/* Send Invoice Modal */}
            {isSendInvoiceOpen && (
                <SendInvoiceModal
                    name={selectedBill?.customerName || ''}
                    identifier={selectedBill?.customerIdentifier || ''}
                    isMobile={selectedBill?.isMobileCustomer || false}
                    paymentId={paymentId?.toString() || ''}
                    paramKey={selectedBill?.groupedBy || ''}
                    paramValue={selectedBill?.id || ''}
                    invoiceNo={selectedBill?.invoiceNo || ''}
                    onClose={() => setSendInvoiceOpen(false)}
                />
            )}
        </>
    );
};

export default OrderWithBillGen;